import { useT } from '../../i18n';
import { cn } from '../../lib/cn';

export interface ProgressBarProps {
  value: number;
  className?: string;
  label?: string;
}

export const ProgressBar = ({ value, className, label }: ProgressBarProps) => {
  const { t } = useT();
  const clamped = Math.max(0, Math.min(100, Math.round(value)));
  return (
    <div
      role="progressbar"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label ?? t('common.progress')}
      className={cn('h-2 w-full overflow-hidden rounded-full bg-sunken', className)}
    >
      <div
        className="h-full rounded-full bg-accent transition-[width] duration-300"
        style={{ width: `${clamped}%` }}
      />
    </div>
  );
};

/** Stacked bar showing how a run's results are distributed. */
export const ResultBar = ({
  passed,
  failed,
  blocked,
  skipped,
  total,
}: {
  passed: number;
  failed: number;
  blocked: number;
  skipped: number;
  total: number;
}) => {
  if (total === 0) return <div className="h-2 w-full rounded-full bg-sunken" />;
  const share = (value: number) => `${(value / total) * 100}%`;

  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-notrun-soft">
      <div className="bg-pass" style={{ width: share(passed) }} title={`${passed} passed`} />
      <div className="bg-fail" style={{ width: share(failed) }} title={`${failed} failed`} />
      <div className="bg-block" style={{ width: share(blocked) }} title={`${blocked} blocked`} />
      <div className="bg-skip" style={{ width: share(skipped) }} title={`${skipped} skipped`} />
    </div>
  );
};
